import React, { useState, useEffect } from "react";
import { db } from "../../../db/firebase";
import {
  doc,
  getDoc,
  onSnapshot,
  collection,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import { useNavigate, useParams } from "react-router-dom";
import AddLeaderModal from "./AddLeaderModal";
import AddMemberModal from "./AddMemberModal";
import { FaPlus, FaUsers, FaCalendarAlt } from "react-icons/fa";
import Card from "../Components/Card";
import AddButtonWithIcon from "../Components/AddButton";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../context/AuthContext";

const GroupDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const { organizationId } = useAuth();
  const [group, setGroup] = useState<any | null>(null);
  const [leaders, setLeaders] = useState<any[]>([]);
  const [members, setMembers] = useState<any[]>([]);
  const [events, setEvents] = useState<any[]>([]);
  const [showLeaderModal, setShowLeaderModal] = useState(false);
  const [showMemberModal, setShowMemberModal] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!organizationId || !id) return;

    const fetchLeaders = async (leaderIds: string[]) => {
      const leaderPromises = leaderIds.map(async (leaderId) => {
        const userDoc = await getDoc(doc(db, "users", leaderId));
        if (userDoc.exists()) {
          return { id: userDoc.id, ...userDoc.data() };
        }
        return null;
      });
      const leaderData = await Promise.all(leaderPromises);
      setLeaders(leaderData.filter((leader) => leader !== null));
    };

    const fetchMembers = async (memberIds: string[]) => {
      const memberPromises = memberIds.map(async (memberId) => {
        const userDoc = await getDoc(doc(db, "users", memberId));
        if (userDoc.exists()) {
          return { id: userDoc.id, ...userDoc.data() };
        }
        return null;
      });
      const memberData = await Promise.all(memberPromises);
      setMembers(memberData.filter((member) => member !== null));
    };

    const fetchEvents = async (groupId: string) => {
      const eventsQuery = query(
        collection(db, "events"),
        where("organizationId", "==", organizationId),
      );
      const querySnapshot = await getDocs(eventsQuery);
      const eventsData = querySnapshot.docs
        .map((eventDoc) => ({
          id: eventDoc.id,
          ...eventDoc.data(),
        }))
        .filter((event) => event.groupOrMinistry === groupId);
      setEvents(eventsData);
    };

    const groupDocRef = doc(db, "groups", id);
    const unsubscribe = onSnapshot(groupDocRef, (groupDoc) => {
      if (
        groupDoc.exists() &&
        groupDoc.data().organizationId === organizationId
      ) {
        const groupData = groupDoc.data();
        setGroup(groupData);
        fetchLeaders(groupData.leaders || []);
        fetchMembers(groupData.members || []);
        fetchEvents(groupDoc.id);
      } else {
        setGroup(null);
        console.error(t("groupNotExistOrNoPermission"));
      }
    });

    return () => unsubscribe();
  }, [id, organizationId, t]);

  const handleAddLeader = () => {
    setShowLeaderModal(true);
  };

  const handleAddMember = () => {
    setShowMemberModal(true);
  };

  const handleLeaderModalClose = () => {
    setShowLeaderModal(false);
  };

  const handleMemberModalClose = () => {
    setShowMemberModal(false);
  };

  const handleEventClick = (eventId: string) => {
    navigate(`/events/${eventId}`);
  };

  return (
    <div className="container mx-auto ">
      {group ? (
        <div className=" ">
          <h1 className="text-3xl font-bold mb-6 text-gray-700">
            {group.name}
          </h1>
          <p className="text-gray-700">{group.description}</p>
          <div className="flex space-x-2 mt-4">
            <AddButtonWithIcon
              onClick={handleAddLeader}
              text={t("addMember")}
              icon={<FaPlus />}
              className="custom-class"
            />
            <AddButtonWithIcon
              onClick={handleAddMember}
              text={t("addMember")}
              icon={<FaPlus />}
              className="custom-class"
            />
          </div>
          <div className="mt-4">
            <h2 className="text-xl font-bold mb-2 text-gray-700">
              {t("leaders")}
            </h2>
            {leaders.length > 0 ? (
              <ul className="space-y-2">
                {leaders.map((leader) => (
                  <Card key={leader.id} className="bg-gray-100 p-4 rounded-2xl">
                    <div className="flex items-center">
                      <FaUsers className="mr-2 text-blue-500" />
                      <div>
                        <p className="text-gray-700">
                          {leader.firstName} {leader.lastName}
                        </p>
                        <p className="text-gray-500">{leader.email}</p>
                      </div>
                    </div>
                  </Card>
                ))}
              </ul>
            ) : (
              <Card>
                <p className="text-gray-600">{t("noLeadersAssigned")}</p>
              </Card>
            )}
          </div>
          <div className="mt-4">
            <h2 className="text-xl font-bold mb-2 text-gray-700">
              {t("members")}
            </h2>
            {members.length > 0 ? (
              <ul className="space-y-2">
                {members.map((member) => (
                  <Card key={member.id} className="bg-gray-100 p-4 rounded-2xl">
                    <div className="flex items-center">
                      <FaUsers className="mr-2 text-green-500" />
                      <div>
                        <p className="text-gray-700">
                          {member.firstName} {member.lastName}
                        </p>
                        <p className="text-gray-500">{member.email}</p>
                      </div>
                    </div>
                  </Card>
                ))}
              </ul>
            ) : (
              <Card>
                <p className="text-gray-600">{t("noMembersAssigned")}</p>
              </Card>
            )}
          </div>
          <div className="mt-4">
            <h2 className="text-xl font-bold mb-2 text-gray-700">
              {t("events")}
            </h2>
            {events.length > 0 ? (
              <ul className="space-y-2">
                {events.map((event) => (
                  <Card
                    key={event.id}
                    onClick={() => handleEventClick(event.id)}
                    className="cursor-pointer bg-gray-100 p-4 rounded-2xl"
                  >
                    <div className="flex items-center">
                      <FaCalendarAlt className="mr-2 text-yellow-500" />
                      <div>
                        <h3 className="text-lg font-bold text-gray-700">
                          {event.title}
                        </h3>
                        <p className="text-gray-500">{event.description}</p>
                        <p className="text-gray-500">
                          {t("date")}: {t("date")}:{" "}
                          {new Date(
                            `${event.date}T00:00:00`
                          ).toLocaleDateString()}
                          {t("time")}: {event.time}
                        </p>
                      </div>
                    </div>
                  </Card>
                ))}
              </ul>
            ) : (
              <Card>
                <p className="text-gray-600">{t("noEventsFoundForGroup")}</p>
              </Card>
            )}
          </div>
        </div>
      ) : (
        <p className="text-gray-700">{t("loading")}</p>
      )}
      {showLeaderModal && group && (
        <AddLeaderModal
          groupId={id!}
          organizationId={group.organizationId}
          onClose={handleLeaderModalClose}
        />
      )}
      {showMemberModal && group && (
        <AddMemberModal
          groupId={id!}
          organizationId={group.organizationId}
          onClose={handleMemberModalClose}
        />
      )}
    </div>
  );
};

export default GroupDetail;
